import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Flag } from "lucide-react";
import DateField from "@/components/DateField";
import TimeField from "@/components/TimeField";

const SPORTS = ["run", "cycle", "swim", "triathlon", "walk", "strength", "team"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const INTENSITIES = ["easy", "moderate", "hard"];

export const dayIndexOf = (iso) => {
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y) return null;
  return (new Date(y, m - 1, d).getDay() + 6) % 7;
};
export const weekStartOf = (iso) => {
  const idx = dayIndexOf(iso);
  if (idx === null) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d - idx);
  const p = (x) => String(x).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

const field = "w-full h-11 bg-black/50 border border-white/15 px-3 text-sm text-white outline-none focus:border-[#E2E30A]";
const label = "text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold";

// 3-step race setup (You → Race week → Race day). Shared by the one-off Race
// Pack flow and the coach race builder.
// Turn a saved race plan back into the form's own shape so an existing plan
// can be edited from step 1 rather than rebuilt from scratch.
const addDays = (iso, n) => {
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y) return "";
  const dt = new Date(y, m - 1, d + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

export default function RaceSetupForm({
  initialProfile = {}, initialPlan = null, athleteName, onSubmit, onCancel, saving = false,
  profileLabel = "The basics", submitLabel = "Build race plan",
}) {
  const rw = initialPlan?.raceWeek || {};
  const fu = rw.fuelling || {};
  const planDays = initialPlan?.trainingWeek?.days || [];
  const planRaceIdx = typeof rw.dayIndex === "number" ? rw.dayIndex : null;
  const planRaceSession = ((planDays[planRaceIdx] || {}).sessions || [])[0] || {};
  const planDur = Number(planRaceSession.duration) || 0;
  const prof = initialPlan?.profile || initialProfile;

  const [step, setStep] = useState(1);
  const [me, setMe] = useState({
    weight: prof.weight ?? "", height: prof.height ?? "",
    age: prof.age ?? "", sex: prof.sex || "male",
    activity: prof.activity || "active",
  });
  const [race, setRace] = useState(() => (initialPlan ? {
    event: rw.event || initialPlan.name || "",
    date: initialPlan.raceDate
      || (planRaceIdx !== null ? addDays(initialPlan.weekStart, planRaceIdx) : ""),
    sport: planRaceSession.sport || "run",
    hours: planDur ? String(Math.floor(planDur / 60)) : "",
    mins: planDur ? String(planDur % 60) : "",
    startTime: rw.startTime || "08:00",
    heat: !!rw.heat,
    carbsPerHour: rw.carbsPerHour ?? "",
    strategy: rw.strategy || "",
    slowerHours: fu.slowerFinishMin ? String(fu.slowerFinishMin / 60) : "",
    practisedPerHr: fu.practisedPerHr ?? "",
    practisedHours: fu.practisedHours ?? "",
    maxGelsPerHour: fu.maxGelsPerHour ?? "",
    maxGelsTotal: fu.maxGelsTotal ?? "",
    srcGel: fu.sources?.gel !== false, srcDrink: fu.sources?.drink !== false,
    srcChews: fu.sources?.chews !== false, srcFood: fu.sources?.food !== false,
  } : {
    event: "", date: "", sport: "run", hours: "", mins: "", startTime: "08:00",
    heat: false, carbsPerHour: "", strategy: "",
    slowerHours: "", practisedPerHr: "", practisedHours: "",
    maxGelsPerHour: "", maxGelsTotal: "",
    srcGel: true, srcDrink: true, srcChews: true, srcFood: true,
  }));
  const [sessions, setSessions] = useState(() => DAYS.map((_, i) => {
    const s = (((planDays[i] || {}).sessions || [])[0]) || {};
    if (i === planRaceIdx || !s.sport) return { sport: "", duration: "", intensity: "easy" };
    return { sport: s.sport, duration: String(s.duration || ""), intensity: s.intensity || "easy" };
  }));

  const raceIdx = dayIndexOf(race.date);
  const raceMinutes = (parseInt(race.hours, 10) || 0) * 60 + (parseInt(race.mins, 10) || 0);
  const step1Ok = me.weight && me.height && me.age;
  const step2Ok = !!race.date && !!race.event;
  const step3Ok = raceMinutes > 0;

  const submit = () => {
    const days = DAYS.map((_, i) => {
      if (i === raceIdx) {
        return { sessions: [{
          sport: race.sport, intensity: "race", duration: raceMinutes,
          isRace: true, heat: race.heat, timing: race.startTime,
        }] };
      }
      const s = sessions[i];
      const dur = parseInt(s.duration, 10) || 0;
      if (!s.sport || !dur) return { sessions: [] };
      return { sessions: [{ sport: s.sport, intensity: s.intensity, duration: dur }] };
    });
    onSubmit({
      name: race.event || "Race Plan",
      weekStart: weekStartOf(race.date),
      unitSystem: "metric",
      goal: "performance",
      profile: {
        weight: Number(me.weight), height: Number(me.height), age: Number(me.age),
        sex: me.sex, activity: me.activity,
      },
      trainingWeek: { days },
      raceWeek: {
        enabled: true, dayIndex: raceIdx, event: race.event,
        startTime: race.startTime, heat: race.heat,
        carbsPerHour: parseInt(race.carbsPerHour, 10) || null,
        strategy: race.strategy,
        // Practised tolerance and willingness — these decide what we plan,
        // not bodyweight or duration alone.
        fuelling: {
          practisedPerHr: parseInt(race.practisedPerHr, 10) || null,
          practisedHours: parseFloat(race.practisedHours) || null,
          maxGelsPerHour: parseInt(race.maxGelsPerHour, 10) || null,
          maxGelsTotal: parseInt(race.maxGelsTotal, 10) || null,
          slowerFinishMin: (parseFloat(race.slowerHours) || 0) * 60 || null,
          sources: {
            gel: race.srcGel, drink: race.srcDrink,
            chews: race.srcChews, food: race.srcFood,
          },
        },
      },
    });
  };

  return (
    <div data-testid="race-setup-form">
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-6">Step {step} / 3</p>

      {step === 1 && (
        <section data-testid="race-setup-step-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Step 1 · {athleteName || "You"}</p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter font-black mt-2">{profileLabel}</h2>
          <p className="text-sm text-zinc-300 mt-2">These numbers drive every target in the plan.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div>
              <p className={label}>Weight (kg)</p>
              <input data-testid="race-weight" type="number" className={`${field} mt-1.5`} value={me.weight}
                onChange={(e) => setMe({ ...me, weight: e.target.value })} placeholder="72" />
            </div>
            <div>
              <p className={label}>Height (cm)</p>
              <input data-testid="race-height" type="number" className={`${field} mt-1.5`} value={me.height}
                onChange={(e) => setMe({ ...me, height: e.target.value })} placeholder="180" />
            </div>
            <div>
              <p className={label}>Age</p>
              <input data-testid="race-age" type="number" className={`${field} mt-1.5`} value={me.age}
                onChange={(e) => setMe({ ...me, age: e.target.value })} placeholder="38" />
            </div>
            <div>
              <p className={label}>Sex</p>
              <select data-testid="race-sex" className={`${field} mt-1.5`} value={me.sex}
                onChange={(e) => setMe({ ...me, sex: e.target.value })}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <p className={label}>Daily activity outside training</p>
              <select data-testid="race-activity" className={`${field} mt-1.5`} value={me.activity}
                onChange={(e) => setMe({ ...me, activity: e.target.value })}>
                <option value="sedentary">Desk job, mostly sitting</option>
                <option value="light">Lightly active</option>
                <option value="active">Active / on feet</option>
                <option value="very_active">Very active / manual work</option>
              </select>
            </div>
          </div>
        </section>
      )}

      {step === 2 && (
        <section data-testid="race-setup-step-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Step 2 · Race week</p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter font-black mt-2">The race</h2>
          <p className="text-sm text-zinc-300 mt-2">We'll build the whole week leading into it.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div>
              <p className={label}>Event name</p>
              <input data-testid="race-event" className={`${field} mt-1.5`} value={race.event}
                onChange={(e) => setRace({ ...race, event: e.target.value })} placeholder="London Marathon" />
            </div>
            <div>
              <p className={label}>Race date</p>
              <div className="mt-1.5">
                <DateField value={race.date} onChange={(v) => setRace({ ...race, date: v })}
                  testid="race-date" placeholder="Pick race day" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <p className={label}>Sport</p>
              <select data-testid="race-sport" className={`${field} mt-1.5`} value={race.sport}
                onChange={(e) => setRace({ ...race, sport: e.target.value })}>
                {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {race.date && (
            <div className="mt-8">
              <p className={label}>Training in the week (optional)</p>
              <p className="text-xs text-zinc-400 mt-1">Add anything still being done — we'll fuel it.</p>
              <div className="mt-3 space-y-2">
                {DAYS.map((d, i) => i === raceIdx ? (
                  <div key={d} className="flex items-center gap-3 border border-[#E2E30A]/40 bg-[#E2E30A]/[0.05] p-3">
                    <span className="w-10 text-xs uppercase tracking-widest text-[#E2E30A] font-bold">{d}</span>
                    <span className="inline-flex items-center gap-2 text-sm text-white">
                      <Flag className="h-3.5 w-3.5 text-[#E2E30A]" /> Race day
                    </span>
                  </div>
                ) : (
                  <div key={d} className="flex flex-wrap items-center gap-2 border border-white/12 p-3">
                    <span className="w-10 text-xs uppercase tracking-widest text-zinc-400 font-bold">{d}</span>
                    <select data-testid={`race-week-sport-${i}`}
                      className="h-9 bg-black/50 border border-white/15 px-2 text-xs text-white outline-none focus:border-[#E2E30A]"
                      value={sessions[i].sport}
                      onChange={(e) => setSessions(sessions.map((s, j) => j === i ? { ...s, sport: e.target.value } : s))}>
                      <option value="">Rest</option>
                      {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input data-testid={`race-week-mins-${i}`} type="number" placeholder="mins"
                      className="h-9 w-20 bg-black/50 border border-white/15 px-2 text-xs text-white outline-none focus:border-[#E2E30A]"
                      value={sessions[i].duration}
                      onChange={(e) => setSessions(sessions.map((s, j) => j === i ? { ...s, duration: e.target.value } : s))} />
                    <select data-testid={`race-week-intensity-${i}`}
                      className="h-9 bg-black/50 border border-white/15 px-2 text-xs text-white outline-none focus:border-[#E2E30A]"
                      value={sessions[i].intensity}
                      onChange={(e) => setSessions(sessions.map((s, j) => j === i ? { ...s, intensity: e.target.value } : s))}>
                      {INTENSITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {step === 3 && (
        <section data-testid="race-setup-step-3">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Step 3 · Race day</p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter font-black mt-2">Race day detail</h2>
          <p className="text-sm text-zinc-300 mt-2">Time on feet sets the whole fuelling plan.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div>
              <p className={label}>Expected finish — hours</p>
              <input data-testid="race-hours" type="number" className={`${field} mt-1.5`} value={race.hours}
                onChange={(e) => setRace({ ...race, hours: e.target.value })} placeholder="3" />
            </div>
            <div>
              <p className={label}>Minutes</p>
              <input data-testid="race-mins" type="number" className={`${field} mt-1.5`} value={race.mins}
                onChange={(e) => setRace({ ...race, mins: e.target.value })} placeholder="30" />
            </div>
            <div>
              <p className={label}>Start time</p>
              <div className="mt-1.5">
                <TimeField value={race.startTime} testid="race-start-time"
                  onChange={(v) => setRace({ ...race, startTime: v })} />
              </div>
            </div>
            <div>
              <p className={label}>Carbs per hour target (optional)</p>
              <input data-testid="race-carbs-hr" type="number" className={`${field} mt-1.5`} value={race.carbsPerHour}
                onChange={(e) => setRace({ ...race, carbsPerHour: e.target.value })} placeholder="auto" />
            </div>
            <div>
              <p className={label}>Slower finish estimate — hours (optional)</p>
              <input data-testid="race-slower-hours" type="number" step="0.5" className={`${field} mt-1.5`} value={race.slowerHours}
                onChange={(e) => setRace({ ...race, slowerHours: e.target.value })} placeholder="e.g. 9" />
            </div>
          </div>

          {/* Practised tolerance and willingness. Without these the plan is
              marked provisional rather than race-ready. */}
          <div className="mt-8 border border-white/12 p-4" data-testid="race-tolerance-block">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#E2E30A] font-bold">Your gut, honestly</p>
            <p className="text-xs text-zinc-300 mt-1.5">
              We plan what you've actually practised, not a textbook number. Leave blank and we'll plan
              conservatively and mark the plan provisional.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <p className={label}>Carbs/hr practised in training</p>
                <input data-testid="race-practised-cph" type="number" className={`${field} mt-1.5`} value={race.practisedPerHr}
                  onChange={(e) => setRace({ ...race, practisedPerHr: e.target.value })} placeholder="e.g. 60" />
              </div>
              <div>
                <p className={label}>…sustained for how many hours?</p>
                <input data-testid="race-practised-hours" type="number" step="0.5" className={`${field} mt-1.5`} value={race.practisedHours}
                  onChange={(e) => setRace({ ...race, practisedHours: e.target.value })} placeholder="e.g. 3" />
              </div>
              <div>
                <p className={label}>Max gels you'll take per hour</p>
                <input data-testid="race-max-gels-hr" type="number" className={`${field} mt-1.5`} value={race.maxGelsPerHour}
                  onChange={(e) => setRace({ ...race, maxGelsPerHour: e.target.value })} placeholder="e.g. 2" />
              </div>
              <div>
                <p className={label}>Max gels for the whole race</p>
                <input data-testid="race-max-gels-total" type="number" className={`${field} mt-1.5`} value={race.maxGelsTotal}
                  onChange={(e) => setRace({ ...race, maxGelsTotal: e.target.value })} placeholder="e.g. 8" />
              </div>
            </div>
            <p className={`${label} mt-5`}>What you'll actually take</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["srcGel", "Gels"], ["srcDrink", "Carb drink"], ["srcChews", "Chews"], ["srcFood", "Real food"]].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`race-source-${k}`}
                  onClick={() => setRace({ ...race, [k]: !race[k] })}
                  className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold border transition-colors"
                  style={{
                    borderColor: race[k] ? "#E2E30A" : "rgba(255,255,255,0.15)",
                    background: race[k] ? "rgba(226,227,10,0.1)" : "transparent",
                    color: race[k] ? "#E2E30A" : "#A1A1AA",
                  }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <label className="sm:col-span-2 flex items-center gap-3 border border-white/12 p-4 cursor-pointer">
              <input data-testid="race-heat" type="checkbox" checked={race.heat}
                onChange={(e) => setRace({ ...race, heat: e.target.checked })}
                className="h-4 w-4 accent-[#E2E30A]" />
              <span className="text-sm text-white">Hot conditions expected (adds sodium + fluid)</span>
            </label>
            <div className="sm:col-span-2">
              <p className={label}>Race notes (optional)</p>
              <textarea data-testid="race-strategy" rows={3} value={race.strategy}
                onChange={(e) => setRace({ ...race, strategy: e.target.value })}
                className="mt-1.5 w-full bg-black/50 border border-white/15 p-3 text-sm text-white outline-none focus:border-[#E2E30A]"
                placeholder="Aid stations every 5k, carrying own gels…" />
            </div>
          </div>
        </section>
      )}

      <div className="mt-10 flex items-center justify-between gap-3">
        <button type="button" data-testid="race-setup-back"
          onClick={() => (step === 1 ? onCancel?.() : setStep(step - 1))}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < 3 ? (
          <button type="button" data-testid="race-setup-next" onClick={() => setStep(step + 1)}
            disabled={step === 1 ? !step1Ok : !step2Ok}
            className="inline-flex items-center gap-2 bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-wider px-6 py-4 transition-colors">
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" data-testid="race-setup-build" onClick={submit}
            disabled={!step3Ok || saving}
            className="inline-flex items-center gap-2 bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-wider px-6 py-4 transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
}
